export const TUTORIAL_SECTIONS = [
    {
        id: "welcome",
        label: "Welcome",
        description: "Introduction to DYC and what the platform can do for you.",
    },
    {
        id: "navigation",
        label: "Navigation",
        description: "Learn how to navigate the app, switch locations, and customize your theme.",
    },
    {
        id: "first-product",
        label: "Your First Product",
        description: "Two ways to add your first product to DYC.",
    },
    {
        id: "marketplace",
        label: "DYC Marketplace",
        description: "Browse products from your connections and submit purchase requests.",
    },
    {
        id: "wholesale-orders",
        label: "Wholesale Orders",
        description: "Manage wholesale marketplace orders — from request through payment and shipping.",
    },
    {
        id: "settings",
        label: "Settings",
        description: "Manage employees, locations, pricing tiers, and your account.",
    },
    {
        id: "lab-orders",
        label: "Lab Orders",
        description: "Order lab tests for your products and view results.",
    },
    {
        id: "color-classification",
        label: "Color Classification",
        description: "Understand DYC's terpene-based color classification system.",
    },
    {
        id: "inventory",
        label: "Inventory",
        description: "Your product dashboard — view, filter, search, and manage products.",
    },
    {
        id: "coa",
        label: "COA Detail",
        description: "View and edit product details including price, weight, and lab results.",
    },
    {
        id: "weigh-in",
        label: "Weigh-In",
        description: "Record weight measurements for your products and track changes over time.",
    },
];

// --- Helper: ensure a panel is open before showing its tutorial step ---
function ensurePanelOpen(toggleSelector, collapsedClass) {
    return () =>
        new Promise((resolve) => {
            const panel = document.querySelector(`.${collapsedClass}`);
            if (panel) {
                const btn = document.querySelector(toggleSelector);
                if (btn) btn.click();
                setTimeout(resolve, 350);
            } else {
                resolve();
            }
        });
}

const ensureDashboardFiltersOpen = ensurePanelOpen(
    ".dash-filter-btn",
    "filter-panel--collapsed"
);

const ensureShowroomFiltersOpen = ensurePanelOpen(
    ".showroom-toggle-btn",
    "filter-panel--collapsed"
);

function ensureOnProductDetail() {
    return new Promise((resolve) => {
        if (window.location.pathname.startsWith("/product/")) {
            resolve();
            return;
        }
        if (window.location.pathname !== "/Dashboard") {
            window.history.pushState(null, "", "/Dashboard");
            window.dispatchEvent(new PopStateEvent("popstate"));
        }
        let attempts = 0;
        const check = () => {
            const row = document.querySelector(".inv-tr--navigable");
            if (row) {
                row.click();
                setTimeout(resolve, 500);
            } else if (attempts < 30) {
                attempts++;
                setTimeout(check, 100);
            } else {
                resolve();
            }
        };
        setTimeout(check, 150);
    });
}

function waitForDashboardTable() {
    return new Promise((resolve) => {
        let attempts = 0;
        const check = () => {
            if (document.querySelector(".inv-tr--navigable")) {
                resolve();
            } else if (attempts < 20) {
                attempts++;
                setTimeout(check, 100);
            } else {
                resolve();
            }
        };
        check();
    });
}

const SETTINGS_TAB_ROUTES = {
    "employee permissions": "/settings/company/employees",
    "locations": "/settings/company/locations",
    "prices": "/settings/company/pricing",
    "company profile": "/settings/company/profile",
    "payments": "/settings/company/payments",
};

function ensureSettingsTab(tabLabel) {
    return () =>
        new Promise((resolve) => {
            const target = SETTINGS_TAB_ROUTES[tabLabel.toLowerCase()];
            if (target && window.location.pathname !== target) {
                window.history.pushState(null, "", target);
                window.dispatchEvent(new PopStateEvent("popstate"));
                setTimeout(resolve, 300);
            } else {
                resolve();
            }
        });
}

// --- Helper: open a modal by clicking its trigger button ---
function ensureModalOpen(buttonSelector, modalSelector) {
    return () =>
        new Promise((resolve) => {
            if (document.querySelector(modalSelector)) {
                resolve();
                return;
            }
            const btn = document.querySelector(buttonSelector);
            if (btn) btn.click();
            let attempts = 0;
            const check = () => {
                if (document.querySelector(modalSelector)) {
                    setTimeout(resolve, 200);
                } else if (attempts < 30) {
                    attempts++;
                    setTimeout(check, 100);
                } else {
                    resolve();
                }
            };
            setTimeout(check, 150);
        });
}

// --- Helper: close a modal if it's open ---
function ensureModalClosed(modalSelector, closeSelector) {
    return () =>
        new Promise((resolve) => {
            if (!document.querySelector(modalSelector)) {
                resolve();
                return;
            }
            const btn = document.querySelector(closeSelector);
            if (btn) btn.click();
            setTimeout(resolve, 300);
        });
}

const ensureColumnPickerOpen = ensureModalOpen(
    ".inv-col-config-btn",
    ".cpm-modal"
);

const ensureColumnPickerClosed = ensureModalClosed(
    ".cpm-modal",
    ".cpm-close"
);

const ensureWeighInModalOpen = ensureModalOpen(
    "[data-tutorial='weigh-in-btn'] button",
    ".wim"
);

const ensureWeighInModalClosed = ensureModalClosed(
    ".wim",
    ".wim__close"
);

// --- Helper: open a showroom product modal ---
function ensureShowroomModalOpen() {
    return new Promise((resolve) => {
        if (document.querySelector("[data-tutorial='showroom-request-form']")) {
            resolve();
            return;
        }
        const card = document.querySelector(".src-card");
        if (card) card.click();
        let attempts = 0;
        const check = () => {
            if (document.querySelector("[data-tutorial='showroom-request-form']")) {
                setTimeout(resolve, 200);
            } else if (attempts < 30) {
                attempts++;
                setTimeout(check, 100);
            } else {
                resolve();
            }
        };
        setTimeout(check, 150);
    });
}

// --- Helper: navigate to a specific page ---
function ensureOnPage(targetPath) {
    return () =>
        new Promise((resolve) => {
            if (window.location.pathname === targetPath) {
                resolve();
                return;
            }
            window.history.pushState(null, "", targetPath);
            window.dispatchEvent(new PopStateEvent("popstate"));
            setTimeout(resolve, 500);
        });
}

const ensureOnOrdersPage = ensureOnPage("/orders");
const ensureOnLabOrdersPage = ensureOnPage("/LabOrders");

export const TUTORIAL_STEPS = [
    // ═══════════════════════════════════════════════════════════════════════
    // PHASE 1 — Getting Started (no products required)
    // ═══════════════════════════════════════════════════════════════════════

    // --- WELCOME ---
    {
        id: "welcome-modal",
        phase: 1,
        section: "welcome",
        target: null,
        title: "Welcome to DYC!",
        description: "Let's take a quick tour of your workspace. We'll walk you through the features available to you based on your role.",
        placement: null,
        route: null,
        spotlightPadding: 0,
        onBeforeShow: null,
        isModal: true,
        requires: { permissions: [], ownerOnly: false },
    },

    // --- NAVIGATION ---
    {
        id: "nav-menu",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='nav-menu']",
        title: "Sidebar Menu",
        description: "This is your main navigation. Click any icon to jump to that section. You can expand or collapse it for more space.",
        placement: "right",
        route: "/Dashboard",
        spotlightPadding: 4,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "nav-inventory",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='nav-inventory']",
        title: "Inventory",
        description: "Takes you to your product dashboard where all your COAs and products are listed.",
        placement: "right",
        route: "/Dashboard",
        spotlightPadding: 4,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "nav-showroom",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='nav-showroom']",
        title: "DYC Marketplace",
        description: "Browse products available from your connections and submit purchase requests.",
        placement: "right",
        route: "/Dashboard",
        spotlightPadding: 4,
        onBeforeShow: null,
        requires: { permissions: ["can_view_showroom"], ownerOnly: false },
    },
    {
        id: "nav-orders",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='nav-orders']",
        title: "Wholesale Orders",
        description: "Manage your wholesale marketplace orders. Track the full lifecycle from request through payment and shipping.",
        placement: "right",
        route: "/Dashboard",
        spotlightPadding: 4,
        onBeforeShow: null,
        requires: { permissions: ["can_manage_orders"], ownerOnly: false },
    },
    {
        id: "nav-documents",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='nav-documents']",
        title: "Documents",
        description: "Access and manage your compliance documents — licenses, permits, and other regulatory files required for your operation.",
        placement: "right",
        route: "/Dashboard",
        spotlightPadding: 4,
        onBeforeShow: null,
        requires: { permissions: ["can_view_compliance_docs"], ownerOnly: false },
    },
    {
        id: "nav-lab-orders",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='nav-lab-orders']",
        title: "Lab Orders",
        description: "Order lab tests for your products from partner labs and view results when they're ready.",
        placement: "right",
        route: "/Dashboard",
        spotlightPadding: 4,
        onBeforeShow: null,
        requires: { permissions: ["can_view_lab_tests"], ownerOnly: false },
    },
    {
        id: "nav-settings",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='nav-settings']",
        title: "Settings",
        description: "Access your account settings, employee permissions, locations, and pricing tiers.",
        placement: "right",
        route: "/Dashboard",
        spotlightPadding: 4,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "header-location-select",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='header-location-select']",
        title: "Location Selector",
        description: "Scope your view to a specific location. When selected, you'll only see products and data for that location.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "header-theme-toggle",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='header-theme-toggle']",
        title: "Theme Toggle",
        description: "Switch between light and dark mode to match your preference.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "notifications-bell",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='notifications-bell']",
        title: "Notifications",
        description: "Alerts about transfer requests, approvals, and other important updates appear here. The badge shows your unread count.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },

    // --- YOUR FIRST PRODUCT (Phase 1 — two ways to get started) ---
    {
        id: "first-product-coa",
        phase: 1,
        section: "first-product",
        target: "[data-tutorial='create-product-btn']",
        title: "Upload a COA",
        description: "The primary way to add products is by uploading a Certificate of Analysis (COA) PDF. Click this button to get started. The system extracts terpene and cannabinoid data and classifies the product automatically.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "first-product-transfer",
        phase: 1,
        section: "first-product",
        target: "[data-tutorial='showroom-grid']",
        title: "Buy Wholesale Products",
        description: "Browse products published by your connections in the DYC Marketplace. Submit purchase requests and receive transfers directly into your inventory.",
        placement: "top",
        route: "/showroom",
        spotlightPadding: 8,
        onBeforeShow: null,
        requires: { permissions: ["can_view_showroom"], ownerOnly: false },
    },

    // --- MARKETPLACE (Phase 1 — browsing doesn't need own products) ---
    {
        id: "showroom-grid",
        phase: 1,
        section: "marketplace",
        target: "[data-tutorial='showroom-grid']",
        title: "Product Listings",
        description: "Browse products published by your connections. Each card shows the terpene profile, price, and available weight.",
        placement: "top",
        route: "/showroom",
        spotlightPadding: 8,
        onBeforeShow: null,
        requires: { permissions: ["can_view_showroom"], ownerOnly: false },
    },
    {
        id: "showroom-filters",
        phase: 1,
        section: "marketplace",
        target: "[data-tutorial='showroom-filters']",
        title: "Marketplace Filters",
        description: "Filter products by terpene profile, product type, color classification, or price range to find exactly what you need.",
        placement: "right",
        route: "/showroom",
        spotlightPadding: 8,
        onBeforeShow: ensureShowroomFiltersOpen,
        requires: { permissions: ["can_view_showroom"], ownerOnly: false },
    },
    {
        id: "showroom-request-form",
        phase: 1,
        section: "marketplace",
        target: "[data-tutorial='showroom-request-form']",
        title: "Request Product",
        description: "Click a product card to open it, then submit a transfer request to the seller specifying your desired quantity.",
        placement: "left",
        route: "/showroom",
        spotlightPadding: 8,
        onBeforeShow: ensureShowroomModalOpen,
        insideModal: true,
        requires: { permissions: ["can_view_showroom"], ownerOnly: false },
    },

    // --- MENU GENERATION (Phase 1 — location-scoped users) ---
    {
        id: "generate-menu",
        phase: 1,
        section: "navigation",
        target: "[data-tutorial='generate-menu-btn']",
        title: "Generate Menu",
        description: "Create a printable product menu to share with customers. With a location selected the menu covers that location; otherwise it covers master inventory.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false, locationScoped: true },
    },

    // --- WHOLESALE ORDERS ---
    {
        id: "orders-filter-bar",
        phase: 1,
        section: "wholesale-orders",
        target: "[data-tutorial='orders-filter-bar']",
        title: "Order Status & Filters",
        description: "Filter orders by status — 'Needs Attention' shows orders requiring your action. Use the role toggle to view only orders where you are buying or selling.",
        placement: "bottom",
        route: "/orders",
        spotlightPadding: 8,
        onBeforeShow: ensureOnOrdersPage,
        requires: { permissions: ["can_manage_orders"], ownerOnly: false },
    },
    {
        id: "orders-table",
        phase: 1,
        section: "wholesale-orders",
        target: "[data-tutorial='orders-table-container']",
        title: "Orders Table",
        description: "Each row shows an order with its status, products, and available actions. Orders follow the lifecycle: Requested → Invoice Sent → Paid → Shipped → Completed.",
        placement: "top",
        route: "/orders",
        spotlightPadding: 8,
        onBeforeShow: null,
        requires: { permissions: ["can_manage_orders"], ownerOnly: false },
    },

    // --- SETTINGS (matches sidebar order: Profile → Payments → Pricing → Employees → Locations) ---
    {
        id: "settings-tabs",
        phase: 1,
        section: "settings",
        target: "[data-tutorial='settings-tabs']",
        title: "Settings",
        description: "Manage your account, employee permissions, locations, and pricing tiers from here.",
        placement: "right",
        route: "/settings",
        spotlightPadding: 8,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: true },
    },
    {
        id: "company-profile",
        phase: 1,
        section: "settings",
        target: "[data-tutorial='company-profile-form']",
        title: "Company Profile",
        description: "Set up your company logo, description, social media links, and marketplace contact email. A complete profile helps buyers find and trust your business.",
        placement: "top",
        route: "/settings/company/profile",
        spotlightPadding: 8,
        onBeforeShow: ensureSettingsTab("Company Profile"),
        requires: { permissions: [], ownerOnly: true },
    },
    {
        id: "payment-settings",
        phase: 1,
        section: "settings",
        target: "[data-tutorial='payment-settings-form']",
        title: "Payment Settings",
        description: "Enter your bank account details (ACH) for receiving wholesale payments. This information is included when sending invoices to buyers.",
        placement: "top",
        route: "/settings/company/payments",
        spotlightPadding: 8,
        onBeforeShow: ensureSettingsTab("Payments"),
        requires: { permissions: [], ownerOnly: true },
    },
    {
        id: "pricing-tiers",
        phase: 1,
        section: "settings",
        target: "[data-tutorial='pricing-tiers']",
        title: "Pricing Tiers",
        description: "Create pricing tiers to offer different rates to different buyers. Assign tiers to products for automatic pricing.",
        placement: "top",
        route: "/settings/company/pricing",
        spotlightPadding: 8,
        onBeforeShow: ensureSettingsTab("Prices"),
        requires: { permissions: ["can_set_prices"], ownerOnly: false },
    },
    {
        id: "user-permissions-table",
        phase: 1,
        section: "settings",
        target: "[data-tutorial='user-permissions-table']",
        title: "Employee Permissions",
        description: "Control what each employee can do — upload COAs, view the marketplace, set prices, and more. Add new employees here too.",
        placement: "top",
        route: "/settings/company/employees",
        spotlightPadding: 8,
        onBeforeShow: ensureSettingsTab("Employee Permissions"),
        requires: { permissions: [], ownerOnly: true },
    },
    {
        id: "locations-list",
        phase: 1,
        section: "settings",
        target: "[data-tutorial='locations-list']",
        title: "Locations",
        description: "Create and manage your physical locations. Employees can be scoped to specific locations to limit their inventory view.",
        placement: "top",
        route: "/settings/company/locations",
        spotlightPadding: 8,
        onBeforeShow: ensureSettingsTab("Locations"),
        requires: { permissions: [], ownerOnly: true },
    },

    // --- LAB ORDERS ---
    {
        id: "lab-orders-new",
        phase: 1,
        section: "lab-orders",
        target: "[data-tutorial='lab-order-new-btn']",
        title: "Create Lab Order",
        description: "Click '+ New Order' to create a lab test order. Select a lab partner, add samples with your desired tests, and submit. You'll receive a Chain of Custody form.",
        placement: "bottom",
        route: "/LabOrders",
        spotlightPadding: 6,
        onBeforeShow: ensureOnLabOrdersPage,
        requires: { permissions: ["can_order_tests"], ownerOnly: false },
    },
    {
        id: "lab-orders-list",
        phase: 1,
        section: "lab-orders",
        target: "[data-tutorial='lab-orders-list']",
        title: "Your Lab Orders",
        description: "View all your lab orders and their status. Expand an order to see individual samples and tests. When results are ready, click 'View COA' to see the lab report.",
        placement: "top",
        route: "/LabOrders",
        spotlightPadding: 8,
        onBeforeShow: null,
        requires: { permissions: ["can_view_lab_tests"], ownerOnly: false },
    },

    // --- PHASE 1 COMPLETION ---
    {
        id: "phase1-complete",
        phase: 1,
        section: "welcome",
        target: null,
        title: "Getting Started Complete!",
        description: "You've covered the basics. Once you have products in your inventory, we'll walk you through managing them. Upload a COA or receive a transfer to get started!",
        placement: null,
        route: "/Dashboard",
        spotlightPadding: 0,
        onBeforeShow: null,
        isModal: true,
        isCompletion: true,
        requires: { permissions: [], ownerOnly: false },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PHASE 2 — Your Products (requires products in inventory)
    // ═══════════════════════════════════════════════════════════════════════

    // --- PHASE 2 WELCOME ---
    {
        id: "phase2-welcome",
        phase: 2,
        section: "welcome",
        target: null,
        title: "Let's Explore Your Products!",
        description: "Now that you have products in your inventory, let's walk through how to manage them — filtering, editing details, and handling orders.",
        placement: null,
        route: null,
        spotlightPadding: 0,
        onBeforeShow: null,
        isModal: true,
        requires: { permissions: [], ownerOnly: false },
    },

    // --- COLOR CLASSIFICATION ---
    {
        id: "color-classification-intro",
        phase: 2,
        section: "color-classification",
        target: null,
        title: "Understanding Color Classification",
        description: "DYC classifies every product into a color based on its terpene profile. This helps buyers quickly understand the product's aroma and effects. Let's explore how this works.",
        placement: null,
        route: null,
        spotlightPadding: 0,
        onBeforeShow: null,
        isModal: true,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "color-distribution-bar",
        phase: 2,
        section: "color-classification",
        target: "[data-tutorial='dashboard-summary-bar']",
        title: "Color Distribution & KPIs",
        description: "The color bar shows your inventory breakdown by terpene classification — click any segment to filter by that color. Below it, KPIs summarize total products, marketplace count, panel completion, and average terpene percentage.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 8,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },

    // --- INVENTORY ---
    {
        id: "inventory-table",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='inventory-table']",
        title: "Product Inventory",
        description: "This table shows all products under your active license. Each row is a COA with its terpene profile, weight, price, and compliance status.",
        placement: "top",
        route: "/Dashboard",
        spotlightPadding: 8,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "inventory-filters",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='inventory-filters']",
        title: "Filters",
        description: "Narrow your inventory by color classification, product type, or panel status. Filters persist across sessions.",
        placement: "right",
        route: "/Dashboard",
        spotlightPadding: 8,
        onBeforeShow: ensureDashboardFiltersOpen,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "inventory-search",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='inventory-search']",
        title: "Search",
        description: "Search across product names, SKUs, and seed-to-sale IDs. Results update as you type.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: ensureDashboardFiltersOpen,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "inventory-menu-btn",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='generate-menu-btn']",
        title: "Generate Menu",
        description: "Create a printable product menu to share with customers. With a location selected the menu covers that location; otherwise it covers master inventory.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false, locationScoped: true },
    },
    {
        id: "inventory-qr-btn",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='qr-code-btn']",
        title: "QR Codes",
        description: "Download compliance badge QR codes for your products. Customers can scan these to view product details and lab results.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "inventory-columns-btn",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='column-picker-btn']",
        title: "Customize Columns",
        description: "Customize which columns appear in your inventory table. Reorder, add, or remove columns to see the data that matters most to you.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "inventory-columns-modal",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='column-picker-body']",
        title: "Column Picker",
        description: "The left panel shows available columns grouped by category. The right panel shows your active columns in display order — drag to reorder, click × to remove.",
        placement: "top",
        route: "/Dashboard",
        spotlightPadding: 8,
        onBeforeShow: ensureColumnPickerOpen,
        insideModal: true,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "dashboard-export",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='export-btn']",
        title: "Export Inventory",
        description: "Export your filtered inventory to a CSV file. Choose which fields to include — product name, terpenes, cannabinoids, pricing, and more.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: ensureColumnPickerClosed,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "dashboard-manage-transfers",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='manage-transfers-btn']",
        title: "Manage Location Transfers",
        description: "Move inventory between your master inventory and locations. Approve or deny location transfer requests from employees.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: ["can_manage_location_transfers"], ownerOnly: false },
    },
    {
        id: "dashboard-request-products",
        phase: 2,
        section: "inventory",
        target: "[data-tutorial='request-products-btn']",
        title: "Request Products",
        description: "When viewing a specific location, request products from the license's master inventory to be transferred to your location.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false, locationScoped: true },
    },

    // --- COA DETAIL ---
    {
        id: "coa-detail",
        phase: 2,
        section: "coa",
        target: "[data-tutorial='coa-detail']",
        title: "COA Detail Page",
        description: "This page shows the full breakdown of a product — terpene wheel, lab panels, pricing, and weight. Click any product from your inventory to get here.",
        placement: "top",
        route: "/Dashboard",
        spotlightPadding: 8,
        onBeforeShow: () => { ensureColumnPickerClosed(); return ensureOnProductDetail(); },
        requires: { permissions: ["can_upload_coa"], ownerOnly: false },
    },
    {
        id: "coa-price",
        phase: 2,
        section: "coa",
        target: "[data-tutorial='coa-price']",
        title: "Edit Price",
        description: "Click the price field to edit it inline. This is the price shown to buyers in the marketplace.",
        placement: "bottom",
        route: null,
        spotlightPadding: 6,
        onBeforeShow: ensureOnProductDetail,
        requires: { permissions: ["can_upload_coa"], ownerOnly: false },
    },
    {
        id: "coa-weight",
        phase: 2,
        section: "coa",
        target: "[data-tutorial='coa-weight']",
        title: "Edit Weight",
        description: "Click the weight field to update available quantity. This is the amount shown as available for transfer.",
        placement: "bottom",
        route: null,
        spotlightPadding: 6, 
        onBeforeShow: ensureOnProductDetail,
        requires: { permissions: ["can_upload_coa"], ownerOnly: false },
    },
    {
        id: "coa-panel-coverage",
        phase: 2,
        section: "coa",
        target: "[data-tutorial='coa-panel-coverage']",
        title: "Panel Coverage",
        description: "Shows which lab test panels have been completed for this product. Safety panels display pass/fail indicators. All panels must pass to list on the marketplace.",
        placement: "top",
        route: null,
        spotlightPadding: 8,
        onBeforeShow: ensureOnProductDetail,
        requires: { permissions: ["can_upload_coa"], ownerOnly: false },
    },
    {
        id: "coa-charts",
        phase: 2, 
        section: "coa",
        target: "[data-tutorial='coa-profiles-row']",
        title: "Cannabinoid & Terpene Charts",
        description: "Visual breakdown of this product's cannabinoid and terpene profiles. Click any compound name to learn more about it.",
        placement: "top",
        route: null,
        spotlightPadding: 8,
        onBeforeShow: ensureOnProductDetail,
        requires: { permissions: ["can_upload_coa"], ownerOnly: false },
    },
    { 
        id: "coa-publish",
        phase: 2,
        section: "coa",
        target: "[data-tutorial='coa-publish-btn']",
        title: "Publish to Marketplace",
        description: "Once all lab panels pass and a quantity is set, publish this product to the DYC Marketplace for your connections to purchase.",
        placement: "top",
        route: null,
        spotlightPadding: 6,
        onBeforeShow: ensureOnProductDetail,
        requires: { permissions: ["can_upload_coa"], ownerOnly: false },
    },
    {
        id: "coa-options-menu",
        phase: 2,
        section: "coa",
        target: "[data-tutorial='coa-options-menu']",
        title: "COA Options",
        description: "Access additional actions: publish to the marketplace, transfer weight to another license, rename, or delete the product.",
        placement: "left",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: waitForDashboardTable,
        requires: { permissions: ["can_see_coa_options"], ownerOnly: false },
    },

    // --- WEIGH-IN ---
    {
        id: "weigh-in",
        phase: 2,
        section: "weigh-in",
        target: "[data-tutorial='weigh-in-btn']",
        title: "Weigh-In",
        description: "After receiving a transfer, use the weigh-in feature to record the actual weight received. This updates your inventory quantities.",
        placement: "bottom",
        route: "/Dashboard",
        spotlightPadding: 6,
        onBeforeShow: null,
        requires: { permissions: [], ownerOnly: false },
    },
    {
        id: "weigh-in-modal",
        phase: 2,
        section: "weigh-in",
        target: "[data-tutorial='wim-body']",
        title: "Weigh-In Form",
        description: "Select products from your inventory, enter the new weight for each, and submit. The system updates your inventory quantities to reflect the actual weight on hand.",
        placement: "left",
        route: "/Dashboard",
        spotlightPadding: 8,
        onBeforeShow: ensureWeighInModalOpen,
        insideModal: true,
        requires: { permissions: [], ownerOnly: false },
    },

    // --- PHASE 2 COMPLETION ---
    {
        id: "tutorial-complete",
        phase: 2,
        section: "welcome",
        target: null,
        title: "You're All Set!",
        description: "You've completed the full DYC tutorial. You can replay any section anytime from the Tutorials tab in Settings.",
        placement: null,
        route: null,
        spotlightPadding: 0,
        onBeforeShow: () => ensureWeighInModalClosed(),
        isModal: true,
        isCompletion: true,
        requires: { permissions: [], ownerOnly: false },
    },
];
